/**
 * QuizTypeBadge — 모든 퀴즈 유형 헤더에서 동일한 배지 행.
 * [유형 라벨 chip] + DifficultyBadge + DebugQuizId
 */
import { cn } from '../../../lib/utils'
import { DebugQuizId, DifficultyBadge } from './debug-quiz-id'
import { useQuizHeaderSuffix, useQuizStyleVariant } from './quiz-style-context'

interface QuizTypeBadgeProps {
  quizId: string | number
  /** 이미 t('types.X.label')로 해석된 라벨 텍스트 */
  typeLabel: string
  difficultyLevel?: number | null
  /** chip 배경색 분기 (adult: 파랑, student: lc-lightblue). 기본 'adult' */
  variant?: 'adult' | 'student'
}

const CHIP_BG: Record<'adult' | 'student', string> = {
  adult: 'bg-blue-500',
  student: 'bg-lc-lightblue-900',
}

export function QuizTypeBadge({ quizId, typeLabel, difficultyLevel, variant = 'adult' }: QuizTypeBadgeProps) {
  const styleVariant = useQuizStyleVariant()
  const headerSuffix = useQuizHeaderSuffix()
  const isLcStudent = styleVariant === 'lc' && variant === 'student'
  const isAiqStudent = styleVariant === 'aiq' && variant === 'student'

  return (
    <div data-quiz-type-badge className="inline-flex gap-2 items-center">
      <div
        className={cn(
          isLcStudent
            ? 'inline-flex items-center text-[16px] font-semibold leading-[1.6] text-lc-text-link'
            : isAiqStudent
              ? 'inline-flex h-[26px] items-center rounded-md bg-lc-key-500 px-2 text-[14px] font-semibold leading-none tracking-[0.2px] text-white'
              : 'inline-flex items-center gap-2 rounded-lg px-3 py-1 text-sm font-bold text-white',
          !isLcStudent && !isAiqStudent && CHIP_BG[variant],
        )}
      >
        <span>{typeLabel}</span>
      </div>
      {isLcStudent ? headerSuffix : null}
      {isAiqStudent && difficultyLevel != null ? (
        <span className="inline-flex h-5 items-center rounded-full bg-lc-key-50 px-2 text-[12px] font-semibold leading-none tracking-[0.3px] text-lc-key-500">
          Lv.{difficultyLevel}
        </span>
      ) : null}
      {!isLcStudent && !isAiqStudent ? <DifficultyBadge level={difficultyLevel} /> : null}
      {!isLcStudent && <DebugQuizId quizId={quizId} />}
    </div>
  )
}
